import React, { useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const SECTIONS = [
  { id: "top", n: "00", label: "Intro" },
  { id: "background", n: "01", label: "Background" },
  { id: "timeline", n: "02", label: "Timeline" },
  { id: "studies", n: "03", label: "Studies" },
  { id: "experience", n: "04", label: "Experience" },
  { id: "products", n: "05", label: "Products" },
  { id: "toolkit", n: "06", label: "Toolkit" },
  { id: "signal", n: "07", label: "Contact" },
];

export const FloatingNav = () => {
  const [activeSection, setActiveSection] = useState("top");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-45% 0px -45% 0px",
      threshold: 0,
    };

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    const handleScrollVisibility = () => {
      const threshold = window.innerHeight * 1.8;
      if (window.scrollY > threshold) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScrollVisibility);
    handleScrollVisibility();

    const handleScrollToTopStart = () => {
      setIsVisible(false);
    };

    window.addEventListener("scroll-to-top-start", handleScrollToTopStart);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScrollVisibility);
      window.removeEventListener("scroll-to-top-start", handleScrollToTopStart);
    };
  }, []);

  const handleScroll = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      if (window.lenis) {
        window.lenis.scrollTo(el, { duration: 1.5 });
      } else {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <div
      data-testid="floating-nav"
      className="floating-nav fixed right-6 top-1/2 -translate-y-1/2 z-[100] hidden lg:flex flex-col items-center gap-4 py-5 px-3 rounded-full border backdrop-blur-md transition-all duration-500"
      style={{
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? "auto" : "none",
        transform: `translateY(-50%) ${isVisible ? "scale(1) translateX(0)" : "scale(0.9) translateX(10px)"}`,
      }}
    >
      {/* Nav dots / numbers */}
      <nav className="flex flex-col items-center gap-4">
        {SECTIONS.map((s) => {
          const isActive = activeSection === s.id;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => handleScroll(e, s.id)}
              className="group relative flex items-center justify-center w-8 h-8"
              aria-label={`Go to ${s.label}`}
            >
              {/* Tooltip Label */}
              <span
                className="absolute right-full mr-3 px-2.5 py-1 rounded text-[0.68rem] font-mono uppercase tracking-microcap pointer-events-none opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 whitespace-nowrap shadow-sm border"
                style={{
                  background: "var(--paper)",
                  borderColor: "var(--rule)",
                  color: "var(--ink)",
                }}
              >
                {s.label}
              </span>

              {/* Dot / Number indicator */}
              <span
                className={`font-mono text-[0.65rem] tracking-tighter transition-all duration-300 flex items-center justify-center rounded-full ${
                  isActive
                    ? "w-7 h-7 border text-white font-medium"
                    : "text-neutral-400 group-hover:text-neutral-900 dark:group-hover:text-white"
                }`}
                style={{
                  borderColor: isActive ? "var(--accent)" : "transparent",
                  color: isActive ? "var(--paper)" : "inherit",
                  backgroundColor: isActive ? "var(--accent)" : "transparent",
                }}
              >
                {s.n}
              </span>
            </a>
          );
        })}
      </nav>
    </div>
  );
};

export default FloatingNav;
